import { isEqual } from "lodash";
import { useEffect, useRef } from "react";
import { useBusniessOfficeNotesContext } from "../../../context/busniessOfficeNotesContext";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { isFcHoldOrg } from "../../../enums/organizationTypes";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { createBusniessOfficeNotesData } from "../utils/createBusniessOfficeNoteData";

/**
 * Syncs business-office notes from the Postgres-backed data-accumulator
 * context into the {@link useBusniessOfficeNotesContext} UI context.
 *
 * Re-runs whenever `generalOrderDetails` or `authOnFileDetails` change,
 * and skips the update when the order ID and raw notes are unchanged.
 *
 * The current `orgId` (via `getOrgIdForFetchExternalWorklist()`) is forwarded
 * to `createBusniessOfficeNotesData` so that org-specific overrides can apply
 * — e.g. Sunstate Urology keeps empty-date placeholder notes (`report_status`
 * + `template_text` only) instead of dropping them.
 *
 * When the order id or raw business-office notes change, clears
 * `writebackQcByReportId` so template-validation QC from a prior order or
 * snapshot cannot display under notes after navigation (provider is route-level).
 *
 * @returns void — operates purely through context setters as a side effect.
 */
export const useUpdateBusinessOfficeNotesContext = () => {
  const { generalOrderDetails, authOnFileDetails } =
    useDataAccumulatorForSingleOrder();
  const lastFcHoldValueRef = useRef<string | undefined>(undefined);

  const {
    setAllCommentsForUI,
    setOrderId,
    setAuthStatus,
    setManualAuthStatus,
    setRawData,
    setWritebackQcByReportId,
    rawData,
    orderId: busniessOfficeNotesOrderId,
  } = useBusniessOfficeNotesContext();

  useEffect(() => {
    if (!generalOrderDetails || !authOnFileDetails) return;

    const busniessOfficeNotes = authOnFileDetails?.auth_on_file?.reports ?? [];
    const isSameOrderId =
      busniessOfficeNotesOrderId === generalOrderDetails?.order?.order_id;
    const isSameBusniessOfficeNotes = isEqual(
      rawData ?? [],
      busniessOfficeNotes ?? [],
    );

    // FC Hold orgs (OCSRI): the radio reflects the order-level FC Hold
    // value (Required/Authorized pre-pick, Auth Initiated post-pick,
    // Pending post-completion) — the same value the write-back RPA writes
    // to the EMR row — not the ingested per-row status. on-pick/completion
    // change only order.status.bo_status (not the reports), so the last
    // applied value is tracked to re-run the override when it flips.
    const orgId = getOrgIdForFetchExternalWorklist();
    const fcHoldValue = isFcHoldOrg(orgId)
      ? generalOrderDetails?.order?.status?.bo_status
      : undefined;
    const fcHoldValueChanged = fcHoldValue !== lastFcHoldValueRef.current;

    if (!isSameOrderId || !isSameBusniessOfficeNotes || fcHoldValueChanged) {
      lastFcHoldValueRef.current = fcHoldValue;
      setWritebackQcByReportId({});
      const templateText = authOnFileDetails?.auth_on_file?.template_text ?? "";
      let commentsData = createBusniessOfficeNotesData(
        busniessOfficeNotes,
        templateText,
        generalOrderDetails?.order?.payload?.date_of_service,
        orgId,
      );
      if (fcHoldValue) {
        commentsData = commentsData.map((group) => ({
          ...group,
          comments: group.comments.map((c) => ({
            ...c,
            boStatus: fcHoldValue,
          })),
        }));
      }
      setAllCommentsForUI(commentsData);
      setOrderId(generalOrderDetails?.order?.order_id ?? "");
      setAuthStatus(authOnFileDetails?.auth_on_file?.auth_status ?? "");
      if (!isSameOrderId) {
        setManualAuthStatus("");
      }
      setRawData(busniessOfficeNotes ?? []);
    }
  }, [
    generalOrderDetails,
    authOnFileDetails,
    busniessOfficeNotesOrderId,
    rawData,
    setWritebackQcByReportId,
  ]);
};
